// KalkulacieMatica — jednoduchá kalkulačná matica (Mirko 24.7.2026, tvar schválený na mockupe).
// Riadky = nákladové položky (ľavý stĺpec), stĺpce = produkty. Bunka = €/m², presne to čo Mirko napíše.
// Bez skrytej matematiky: žiadna automatická +5 % rezerva (Mirkovo rozhodnutie 24.7.).
// Náklad sa dá pomenovať ručne ALEBO vybrať z PLU číselníka.
import React, { useState, useMemo, useRef } from 'react'
import { COLORS, SPACE, RADIUS, TYPO, CELL, SK } from '../theme.js'
import { fetchAuth } from '../auth/fetchAuth.js'
import { freshRow, freshCol, colNaklad, colStats, sumMatica } from '../lib/kalkMatica.js'

const e2 = (v) => SK.fmt(Number(v) || 0, 2)
const e0 = (v) => SK.fmt(Number(v) || 0, 0)

const W_NAZOV = 300, W_PREPOCET = 240, W_COL = 132

const cellBase = {
  padding: '4px 7px', fontSize: 12, fontFamily: TYPO.mono, textAlign: 'right',
  border: '1px solid transparent', borderRadius: 4, width: '100%', boxSizing: 'border-box',
  background: 'transparent', color: COLORS.text,
}
const inputCell = { ...cellBase, background: CELL.input.bg, border: `1px solid ${CELL.input.border}` }

// Bunka s číslom — drží si vlastný text, zapisuje až na blur/Enter (inak by pri každom znaku preskakoval kurzor).
function NumCell({ value, onCommit, readOnly, align, placeholder, style }) {
  const [txt, setTxt] = useState(null)
  const shown = txt != null ? txt : (value === '' || value == null ? '' : String(value).replace('.', ','))
  const commit = () => { if (txt != null) { onCommit(txt.trim()); setTxt(null) } }
  return (
    <input
      value={shown} disabled={readOnly} placeholder={placeholder || ''}
      onChange={e => setTxt(e.target.value)}
      onFocus={e => e.target.select()}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') { commit(); e.target.blur() } if (e.key === 'Escape') { setTxt(null); e.target.blur() } }}
      style={{ ...inputCell, textAlign: align || 'right', ...(style || {}) }}
    />
  )
}

// Textové pole (názov riadku/stĺpca, prepočet, kg)
function TxtCell({ value, onCommit, readOnly, placeholder, style }) {
  const [txt, setTxt] = useState(null)
  const shown = txt != null ? txt : (value || '')
  const commit = () => { if (txt != null) { onCommit(txt); setTxt(null) } }
  return (
    <input
      value={shown} disabled={readOnly} placeholder={placeholder || ''}
      onChange={e => setTxt(e.target.value)} onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') { commit(); e.target.blur() } if (e.key === 'Escape') { setTxt(null); e.target.blur() } }}
      style={{ ...cellBase, textAlign: 'left', background: 'transparent', border: '1px solid transparent', ...(style || {}) }}
    />
  )
}

// Výber položky z PLU číselníka — pomenuje náklad a zapamätá si cenu (Mirko: "aj z číselníka PLU ale aj vlastný")
function PluVyber({ onPick, onClose }) {
  const [q, setQ] = useState('')
  const [rows, setRows] = useState([])
  const [busy, setBusy] = useState(false)
  const tRef = useRef(null)
  const hladaj = (val) => {
    setQ(val); clearTimeout(tRef.current)
    if (!val.trim()) { setRows([]); return }
    setBusy(true)
    tRef.current = setTimeout(() => {
      fetchAuth('/api/kalkulacie/plu/search?q=' + encodeURIComponent(val)).then(r => r.json())
        .then(d => setRows(Array.isArray(d) ? d : [])).catch(() => {}).finally(() => setBusy(false))
    }, 250)
  }
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 18, width: 'min(560px,94vw)', maxHeight: '80vh', overflowY: 'auto' }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>Vybrať náklad z PLU</h3>
        <div style={{ fontSize: 12, color: COLORS.muted, marginBottom: 10 }}>Doplní názov položky a zapamätá cenu z cenníka. Hodnotu na m² si zadáš sám.</div>
        <input autoFocus value={q} onChange={e => hladaj(e.target.value)} placeholder="hľadaj (páska, baumit, cement, sieťka…)"
          style={{ ...cellBase, textAlign: 'left', background: '#fff', border: `1px solid ${COLORS.line}`, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit' }} />
        <div style={{ marginTop: 10, border: `1px solid ${COLORS.line}`, borderRadius: 8 }}>
          {busy && <div style={{ padding: 12, color: COLORS.muted, fontSize: 12 }}>Hľadám…</div>}
          {!busy && !rows.length && q.trim() && <div style={{ padding: 12, color: COLORS.muted, fontSize: 12 }}>Nič nenájdené.</div>}
          {rows.map(r => (
            <div key={r.plu} onClick={() => onPick(r)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderBottom: `1px solid ${COLORS.line}`, cursor: 'pointer' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12.5, color: COLORS.text }}>{r.name}</div>
                <div style={{ fontSize: 10.5, color: COLORS.muted, fontFamily: TYPO.mono }}>PLU {r.plu} · {r.unit}</div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 700 }}>{e2(r.last_price)} €<span style={{ fontSize: 10, color: COLORS.muted }}>/{r.unit}</span></div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'right', marginTop: 10 }}>
          <button onClick={onClose} style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${COLORS.line}`, background: '#fff', cursor: 'pointer', fontSize: 12.5 }}>Zavrieť</button>
        </div>
      </div>
    </div>
  )
}

const miniBtn = { border: `1px solid ${COLORS.line}`, background: '#fff', borderRadius: 5, cursor: 'pointer', fontSize: 10.5, padding: '1px 5px', color: COLORS.muted, lineHeight: 1.5 }

export default function KalkulacieMatica({ matica, onChange, readOnly }) {
  const [pluFor, setPluFor] = useState(null)   // id riadku, pre ktorý vyberáme PLU
  const [showPrepocet, setShowPrepocet] = useState(true)

  const rows = (matica && matica.rows) || []
  const cols = (matica && matica.cols) || []

  const patch = (m) => { if (!readOnly) onChange(m) }
  const setRow = (id, p) => patch({ ...matica, rows: rows.map(r => r.id === id ? { ...r, ...p } : r) })
  const setCol = (id, p) => patch({ ...matica, cols: cols.map(c => c.id === id ? { ...c, ...p } : c) })
  const setCell = (colId, rowId, val) => patch({ ...matica, cols: cols.map(c => c.id === colId ? { ...c, v: { ...(c.v || {}), [rowId]: val } } : c) })

  const addRow = () => patch({ ...matica, rows: [...rows, freshRow()] })
  const delRow = (id) => {
    const r = rows.find(x => x.id === id)
    const pouzity = cols.some(c => String((c.v || {})[id] ?? '').trim() !== '')
    if (pouzity && !window.confirm('Riadok „' + (r ? r.l : '') + '" má vyplnené hodnoty. Zmazať aj s nimi?')) return
    patch({ ...matica, rows: rows.filter(x => x.id !== id), cols: cols.map(c => { const v = { ...(c.v || {}) }; delete v[id]; return { ...c, v } }) })
  }
  const moveRow = (id, dir) => {
    const i = rows.findIndex(r => r.id === id); const j = i + dir
    if (i < 0 || j < 0 || j >= rows.length) return
    const nr = rows.slice(); const t = nr[i]; nr[i] = nr[j]; nr[j] = t
    patch({ ...matica, rows: nr })
  }
  const addCol = () => patch({ ...matica, cols: [...cols, freshCol('Produkt ' + (cols.length + 1))] })
  const dupCol = (id) => {
    const c = cols.find(x => x.id === id); if (!c) return
    patch({ ...matica, cols: [...cols, { ...c, id: freshCol().id, l: c.l + ' (kópia)', v: { ...(c.v || {}) } }] })
  }
  const delCol = (id) => {
    const c = cols.find(x => x.id === id)
    if (!window.confirm('Zmazať stĺpec „' + (c ? c.l : '') + '"?')) return
    patch({ ...matica, cols: cols.filter(x => x.id !== id) })
  }

  const stats = useMemo(() => cols.map(c => colStats(c, rows)), [cols, rows])
  const spolu = useMemo(() => sumMatica(matica), [matica])

  const gridCols = `${W_NAZOV}px ${showPrepocet ? W_PREPOCET + 'px' : ''} repeat(${cols.length}, ${W_COL}px)`
  const cellPad = { padding: '3px 6px', display: 'flex', alignItems: 'center' }
  const stickyL = { position: 'sticky', left: 0, zIndex: 2, background: '#fff' }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm, marginBottom: 6, flexWrap: 'wrap' }}>
        <b style={{ fontSize: 13 }}>Rozpad ceny</b>
        <span style={{ fontSize: 11, color: COLORS.muted }}>bunka = € na m², súčet stĺpca je jednotková cena</span>
        <span style={{ flex: 1 }} />
        <button type="button" onClick={() => setShowPrepocet(s => !s)} style={miniBtn}>{showPrepocet ? 'Skryť prepočet' : 'Zobraziť prepočet'}</button>
        {!readOnly && <button type="button" onClick={addRow} style={miniBtn}>+ riadok</button>}
        {!readOnly && <button type="button" onClick={addCol} style={miniBtn}>+ produkt</button>}
      </div>

      <div style={{ overflowX: 'auto', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md || 8 }}>
        <div style={{ minWidth: 'fit-content' }}>
          {/* hlavička */}
          <div style={{ display: 'grid', gridTemplateColumns: gridCols, background: CELL.header.bg, borderBottom: `1px solid ${COLORS.line}` }}>
            <div style={{ ...cellPad, ...stickyL, background: CELL.header.bg, fontSize: 11, fontWeight: 700, color: CELL.header.text }}>Náklad</div>
            {showPrepocet && <div style={{ ...cellPad, fontSize: 11, fontWeight: 700, color: CELL.header.text }}>Prepočet</div>}
            {cols.map(c => (
              <div key={c.id} style={{ ...cellPad, flexDirection: 'column', alignItems: 'stretch', gap: 1, padding: '4px 5px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <TxtCell value={c.l} readOnly={readOnly} onCommit={v => setCol(c.id, { l: v })} placeholder="názov" style={{ fontSize: 11.5, fontWeight: 700, padding: '2px 4px', fontFamily: 'inherit' }} />
                  {!readOnly && <button type="button" title="Kópia stĺpca" onClick={() => dupCol(c.id)} style={{ ...miniBtn, padding: '0 4px' }}>⧉</button>}
                  {!readOnly && cols.length > 1 && <button type="button" title="Zmazať stĺpec" onClick={() => delCol(c.id)} style={{ ...miniBtn, padding: '0 4px' }}>✕</button>}
                </div>
                <TxtCell value={c.kg} readOnly={readOnly} onCommit={v => setCol(c.id, { kg: v })} placeholder="kg / m² (voliteľné)" style={{ fontSize: 10, padding: '1px 4px', color: COLORS.muted, fontFamily: 'inherit' }} />
              </div>
            ))}
          </div>

          {/* riadky nákladov */}
          {rows.map((r, i) => (
            <div key={r.id} style={{ display: 'grid', gridTemplateColumns: gridCols, background: i % 2 ? CELL.zebra.bg : 'transparent', borderBottom: `1px solid ${COLORS.line}` }}>
              <div style={{ ...cellPad, ...stickyL, background: i % 2 ? '#f7f7f7' : ('#fff'), flexDirection: 'column', alignItems: 'stretch', gap: 3, padding: '5px 8px' }}>
                <TxtCell value={r.l} readOnly={readOnly} onCommit={v => setRow(r.id, { l: v })} placeholder="názov nákladu" style={{ fontSize: 12.5, fontFamily: 'inherit', fontWeight: 600 }} />
                {!readOnly && (
                  <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                    <button type="button" title="Vybrať z PLU" onClick={() => setPluFor(r.id)} style={{ ...miniBtn, color: r.plu ? COLORS.add : COLORS.muted, borderColor: r.plu ? COLORS.add : COLORS.line }}>PLU</button>
                    <button type="button" title="Vyššie" onClick={() => moveRow(r.id, -1)} style={{ ...miniBtn, padding: '0 5px' }}>↑</button>
                    <button type="button" title="Nižšie" onClick={() => moveRow(r.id, 1)} style={{ ...miniBtn, padding: '0 5px' }}>↓</button>
                    <button type="button" title="Zmazať riadok" onClick={() => delRow(r.id)} style={{ ...miniBtn, padding: '0 6px' }}>−</button>
                  </div>
                )}
              </div>
              {showPrepocet && (
                <div style={{ ...cellPad, flexDirection: 'column', alignItems: 'stretch' }}>
                  <TxtCell value={r.prepocet} readOnly={readOnly} onCommit={v => setRow(r.id, { prepocet: v })} placeholder="napr. 15 € / 200 m²" style={{ fontSize: 12.5, color: COLORS.text, fontFamily: 'inherit' }} />
                  {r.plu && <div style={{ fontSize: 9.5, color: COLORS.muted, fontFamily: TYPO.mono, paddingLeft: 7 }}>PLU {r.plu} · {e2(r.pluPrice)} €/{r.pluUnit || 'ks'}</div>}
                </div>
              )}
              {cols.map(c => (
                <div key={c.id} style={cellPad}>
                  <NumCell value={(c.v || {})[r.id]} readOnly={readOnly} onCommit={v => setCell(c.id, r.id, v)} />
                </div>
              ))}
            </div>
          ))}

          {/* päta — súčet, predajná, zisk */}
          <div style={{ display: 'grid', gridTemplateColumns: gridCols, background: CELL.sum.bg, borderTop: `2px solid ${COLORS.line}` }}>
            <div style={{ ...cellPad, ...stickyL, background: '#eaf7ef', fontWeight: 700, fontSize: 12, color: CELL.sum.text }}>Náklady na m²</div>
            {showPrepocet && <div style={cellPad} />}
            {stats.map((s, i) => (
              <div key={cols[i].id} style={{ ...cellPad, justifyContent: 'flex-end', fontFamily: TYPO.mono, fontWeight: 700, fontSize: 12.5, color: CELL.sum.text }}>{e2(s.naklad)} €</div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: gridCols, borderBottom: `1px solid ${COLORS.line}` }}>
            <div style={{ ...cellPad, ...stickyL, fontWeight: 700, fontSize: 12 }}>Predajná cena</div>
            {showPrepocet && <div style={cellPad} />}
            {cols.map(c => (
              <div key={c.id} style={cellPad}><NumCell value={c.predajna} readOnly={readOnly} onCommit={v => setCol(c.id, { predajna: v })} style={{ fontWeight: 700 }} /></div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: gridCols, background: CELL.final.bg }}>
            <div style={{ ...cellPad, ...stickyL, background: '#fdf3e3', fontWeight: 700, fontSize: 12, color: CELL.final.text }}>Zisk na m²</div>
            {showPrepocet && <div style={cellPad} />}
            {stats.map((s, i) => (
              <div key={cols[i].id} style={{ ...cellPad, justifyContent: 'flex-end', fontFamily: TYPO.mono, fontWeight: 700, fontSize: 12.5, color: s.zisk >= 0 ? CELL.sum.text : COLORS.remove }}>
                {s.zisk >= 0 ? '+' : ''}{e2(s.zisk)} €
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Blok 2 — na stavbu */}
      <div style={{ marginTop: SPACE.lg }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Na stavbu <span style={{ fontWeight: 400, fontSize: 11, color: COLORS.muted }}>doplň výmeru, zvyšok dopočítam</span></div>
        <div style={{ overflowX: 'auto', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md || 8 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12, minWidth: 560 }}>
            <thead>
              <tr style={{ background: CELL.header.bg, color: CELL.header.text }}>
                <th style={{ textAlign: 'left', padding: '5px 8px', fontSize: 11 }}>Produkt</th>
                <th style={{ textAlign: 'right', padding: '5px 8px', fontSize: 11, width: 110 }}>Výmera m²</th>
                <th style={{ textAlign: 'right', padding: '5px 8px', fontSize: 11 }}>Nákladová cena</th>
                <th style={{ textAlign: 'right', padding: '5px 8px', fontSize: 11 }}>Tržba</th>
                <th style={{ textAlign: 'right', padding: '5px 8px', fontSize: 11 }}>Zisk</th>
              </tr>
            </thead>
            <tbody>
              {cols.map((c, i) => (
                <tr key={c.id} style={{ borderBottom: `1px solid ${COLORS.line}`, background: i % 2 ? CELL.zebra.bg : 'transparent' }}>
                  <td style={{ padding: '4px 8px' }}>{c.l}{c.kg ? <span style={{ color: COLORS.muted, fontSize: 10.5 }}> · {c.kg}</span> : null}</td>
                  <td style={{ padding: '3px 6px' }}><NumCell value={c.vymera} readOnly={readOnly} onCommit={v => setCol(c.id, { vymera: v })} /></td>
                  <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: TYPO.mono }}>{e0(stats[i].nakladCena)} €</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: TYPO.mono }}>{e0(stats[i].trzba)} €</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: TYPO.mono, fontWeight: 700, color: stats[i].ziskSpolu >= 0 ? CELL.sum.text : COLORS.remove }}>{stats[i].ziskSpolu >= 0 ? '+' : ''}{e0(stats[i].ziskSpolu)} €</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: CELL.final.bg, color: CELL.final.text, fontWeight: 700 }}>
                <td style={{ padding: '6px 8px' }}>Spolu</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: TYPO.mono }}>{e0(cols.reduce((a, c) => a + (Number(String(c.vymera).replace(',', '.')) || 0), 0))}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: TYPO.mono }}>{e0(spolu.n)} €</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: TYPO.mono }}>{e0(spolu.t)} €</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: TYPO.mono }}>{spolu.z >= 0 ? '+' : ''}{e0(spolu.z)} €</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {pluFor && (
        <PluVyber
          onClose={() => setPluFor(null)}
          onPick={(p) => {
            setRow(pluFor, { l: p.name || 'Položka', plu: p.plu, pluName: p.name, pluPrice: p.last_price, pluUnit: p.unit })
            setPluFor(null)
          }}
        />
      )}
    </div>
  )
}
